import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { Row, Card, Form, Col, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import {
  leerPaises,
  Pais,
  Ciudad,
  useLeerSectoresOrigen,
} from "../../../services/paisesServicios";
import { crearNuevoPropietario } from "./actions";
import {
  TipoDocumento,
  CanalMarketing,
} from "../../../services/propietariosServicios";
import ValidationError from "../../../services/ValidationError";
import SuccessButton from "../../organisms/SuccessButton";
import ServerValidationErrorAlert from "../../organisms/ServerValidationErrorAlert";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import ServerErrorAlert from "../../organisms/ServerErrorAlert";
import LoadingScreen from "../../organisms/LoadingScreen";
import {
  FormState,
  FormValue,
  initialFormState,
  invalidateStates,
} from "./FormState";
import { useNavigate } from "react-router-dom";
import { RUTA_PROPIETARIOS } from "../../../services/rutas";
import { capitalize } from "../../../utils/textUtils";
import { ValidationErrorResponse } from "../../../services/ValidationError";
import { StyledFormControl } from "../../organisms/StyledFormControl";
import ModalFirma from "./ModalFirma";
import CardTitle from "../../organisms/CardTitle";
import Exito, { ExitoOption } from "../../organisms/Exito";

function FormSection() {
  const [validatedForm, setValidatedForm] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [nuevoPropietarioId, setNuevoPropietarioId] = useState("");
  const [paises, setPaises] = useState([] as Array<Pais>);
  const [serverFailedValidations, setServerFailedValidations] = useState(
    {} as ValidationErrorResponse
  );
  const [serverErrorOcurred, setServerErrorOcurred] = useState(false);
  const [crearMascota, setCrearMascota] = useState(false);
  const [firmar, setFirmar] = useState(true);
  const [mostrarModalFirma, setMostrarModalFirma] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const { data: sectoresResidencia = [] } = useLeerSectoresOrigen();

  let formRef = useRef({} as HTMLFormElement);
  let navigate = useNavigate();

  const paisProveniencia = useMemo(() => {
    if (paises && paises instanceof Array && paises.length > 0) {
      return paises?.find(
        (pais, i) => pais.id === formState.paisProveniencia.value
      );
    }
    return null;
  }, [paises, formState.paisProveniencia.value]);

  const ciudadesProveniencia = useMemo(() => {
    if (!paises) {
      return [];
    }
    if (paisProveniencia) {
      const ciudades = paisProveniencia.departamentos.flatMap(
        (dpt) => dpt.ciudades
      );
      if (ciudades) {
        return ciudades;
      } else {
        return [];
      }
    }
    return [] as Array<Ciudad>;
  }, [paisProveniencia, paises]);

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;

    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      save(crearMascota);
    }

    setValidatedForm(true);
  };

  useEffect(() => {
    async function poblarPaises() {
      let respuestaPaises: Array<Pais> | undefined = await leerPaises();
      if (respuestaPaises) {
        const colombia = respuestaPaises.find(
          (_pais) => _pais.nombre === "Colombia"
        );
        if (colombia) {
          const colombiaIndex = respuestaPaises.indexOf(colombia);
          respuestaPaises.splice(colombiaIndex, 1);
          respuestaPaises.splice(0, 0, colombia);
        }
        setPaises(respuestaPaises);
      }
    }
    poblarPaises();
  }, [setPaises]);

  useEffect(() => {
    if (nuevoPropietarioId) {
      if (crearMascota) {
        navigate(`${RUTA_PROPIETARIOS}/${nuevoPropietarioId}/mascotas/crear`, {
          replace: false,
        });
      } else {
        setIsFinished(true);
      }
    }
  }, [crearMascota, nuevoPropietarioId, navigate]);

  const onTypeAheadChanged = (
    selected: any[],
    name: string,
    options: any[]
  ) => {
    if (selected && selected.length > 0) {
      const selectedValueId = selected[0]["id"];
      const validValue = options.find((opt) => opt["id"] === selectedValueId);
      const newInputState = {
        ...formState[name as keyof FormState],
        value: selectedValueId,
        isValid: validValue,
        wasTouched: true,
      };
      setFormState({ ...formState, [name]: newInputState });
    } else {
      const newInputState = {
        ...formState[name as keyof FormState],
        value: "",
        isValid: true,
        wasTouched: true,
      };
      setFormState({ ...formState, [name]: newInputState });
    }
  };

  const onInputChanged = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      value: target.value,
      isValid: target.checkValidity(),
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onInputChangedCapital = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      value: capitalize(target.value),
      isValid: target.checkValidity(),
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onCheckChanged = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      value: !formState[target.name as keyof FormState].value,
      isValid: target.checkValidity(),
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onSignClean = () => {
    const key = "firma" as keyof FormState;
    const newInputState = {
      ...formState[key],
      value: "",
      isValid: true,
      wasTouched: true,
    };
    setFormState({ ...formState, [key]: newInputState });
  };

  const onSignConfirmed = (dataUrl?: string) => {
    const key = "firma" as keyof FormState;
    const newInputState = {
      ...formState[key],
      value: dataUrl,
      isValid: true,
      wasTouched: true,
    };
    setFormState({ ...formState, [key]: newInputState });
  };

  const onFileChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      const newInputState = {
        ...formState[event.currentTarget.name as keyof FormState],
        value: selectedFile,
        isValid: event.currentTarget.checkValidity(),
      };
      setFormState({ ...formState, [event.currentTarget.name]: newInputState });
    }
  };

  const handleBlur = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      wasTouched: true,
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onCrearMascotaButtonClicked = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setCrearMascota(true);
  };

  const save = async (crearMascota: Boolean) => {
    setServerFailedValidations({} as ValidationErrorResponse);
    setServerErrorOcurred(false);
    setIsLoading(true);
    try {
      const response = await crearNuevoPropietario(formState);
      setIsLoading(false);
      setNuevoPropietarioId(response.id);
    } catch (error: any) {
      if (error instanceof ValidationError) {
        invalidateStates(formState, error.getFailedValidations());
        setServerFailedValidations(error.getFailedValidations());
      } else {
        setServerErrorOcurred(true);
      }
    }

    setIsLoading(false);
  };

  const onToggleFirmar = () => {
    const newFirmaState = {
      ...formState["firma"],
      value: "",
      isValid: undefined,
      wasTouched: false,
    };
    setFormState({ ...formState, firma: newFirmaState });

    const newContratoState = {
      ...formState["contrato"],
      value: undefined,
      isValid: undefined,
      wasTouched: false,
    };
    setFormState({ ...formState, contrato: newContratoState });
    setFirmar(!firmar);
  };

  if (isFinished && nuevoPropietarioId) {
    return (
      <Exito
        title={"¡Creación exitosa!"}
        options={buildExitoOptions(nuevoPropietarioId)}
      />
    );
  }

  return (
    <Row className="justify-content-center">
      <div className="col-xs-12 col-md-10 col-lg-8 col-xxl-5">
        <LoadingScreen isLoading={isLoading} />
        <Card>
          <StyledCardHeader className="pt-4 pb-4">
            <CardTitle>Crea Un Propietario</CardTitle>
          </StyledCardHeader>
          <Card.Body>
            {serverFailedValidations &&
              Object.keys(serverFailedValidations).length > 0 && (
                <Row className="justify-content-center mb-3">
                  <ServerValidationErrorAlert
                    serverFailedValidations={serverFailedValidations}
                    wrapperClass="col col-9"
                  />
                </Row>
              )}

            {serverErrorOcurred && (
              <Row className="justify-content-center mb-3">
                <ServerErrorAlert class="col col-9" />
              </Row>
            )}

            {serverErrorOcurred && <ServerErrorModal />}

            <Row className="justify-content-center">
              <Form
                className="col col-9"
                noValidate
                validated={validatedForm}
                onSubmit={handleSubmit}
                ref={formRef}
              >
                <h5 className="mb-3">Datos Básicos</h5>
                <Form.Group
                  as={Col}
                  controlId="formPrimerNombre"
                  className="mb-2"
                >
                  <Form.Label>Primer Nombre</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="primerNombre"
                    value={formState.primerNombre.value}
                    onChange={onInputChangedCapital}
                    required
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.primerNombre)}
                    isInvalid={isFormValueInvalid(formState.primerNombre)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formSegundoNombre"
                  className="mb-2"
                >
                  <Form.Label>Segundo Nombre</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="segundoNombre"
                    value={formState.segundoNombre.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.segundoNombre)}
                    isInvalid={isFormValueInvalid(formState.segundoNombre)}
                  />
                  <Form.Text muted>Opcional</Form.Text>
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formPrimerApellido"
                  className="mb-2"
                >
                  <Form.Label>Primer Apellido</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="primerApellido"
                    value={formState.primerApellido.value}
                    onChange={onInputChangedCapital}
                    required
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.primerApellido)}
                    isInvalid={isFormValueInvalid(formState.primerApellido)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formSegundoApellido"
                  className="mb-2"
                >
                  <Form.Label>Segundo Apellido</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="segundoApellido"
                    value={formState.segundoApellido.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.segundoApellido)}
                    isInvalid={isFormValueInvalid(formState.segundoApellido)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} controlId="formFechaNac" className="mb-5">
                  <Form.Label>Fecha de Nacimiento</Form.Label>
                  <StyledFormControl
                    type="date"
                    name="fechaNacimiento"
                    value={formState.fechaNacimiento.value}
                    onChange={onInputChanged}
                    isValid={isFormValueValid(formState.fechaNacimiento)}
                    isInvalid={isFormValueInvalid(formState.fechaNacimiento)}
                  />
                  <Form.Text muted>Opcional</Form.Text>
                </Form.Group>

                <h5 className="mb-3">Datos de Identificación</h5>
                <Form.Group as={Col} controlId="formTipoDoc" className="mb-3">
                  <Form.Label>Tipo Documento</Form.Label>
                  <Form.Check
                    type="radio"
                    id="radio_cc"
                    label="Cédula"
                    value={TipoDocumento.Cedula.toString()}
                    name="tipoDocumento"
                    required
                    onChange={onInputChanged}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_ti"
                    label="Tarjeta Identidad"
                    value={TipoDocumento.TarjetaIdentidad.toString()}
                    name="tipoDocumento"
                    required
                    onChange={onInputChanged}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_ce"
                    label="Cédula Extranjería"
                    value={TipoDocumento.CedulaExtranjeria.toString()}
                    name="tipoDocumento"
                    required
                    onChange={onInputChanged}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_pas"
                    label="Pasaporte"
                    value={TipoDocumento.Pasaporte.toString()}
                    name="tipoDocumento"
                    required
                    onChange={onInputChanged}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} controlId="formDoc" className="mb-2">
                  <Form.Label>No. Documento</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="documento"
                    value={formState.documento.value}
                    onChange={onInputChanged}
                    required
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.documento)}
                    isInvalid={isFormValueInvalid(formState.documento)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Documento debe ser único.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} controlId="formCiudadExp" className="mb-5">
                  <Form.Label>Ciudad Expedición</Form.Label>
                  <StyledFormControl
                    type="text"
                    value={formState.ciudadExpedicion.value}
                    name="ciudadExpedicion"
                    onChange={onInputChanged}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.ciudadExpedicion)}
                    isInvalid={isFormValueInvalid(formState.ciudadExpedicion)}
                  />
                </Form.Group>

                <h5 className="mb-3">Datos de Contacto</h5>
                <Form.Group as={Col} controlId="formEmail" className="mb-2">
                  <Form.Label>Email</Form.Label>
                  <StyledFormControl
                    type="email"
                    name="email"
                    value={formState.email.value}
                    onChange={onInputChanged}
                    required
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.email)}
                    isInvalid={isFormValueInvalid(formState.email)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado, único y con formato de email.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} controlId="formCelular" className="mb-2">
                  <Form.Label>Celular</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="celular"
                    value={formState.celular.value}
                    onChange={onInputChanged}
                    required
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.celular)}
                    isInvalid={isFormValueInvalid(formState.celular)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formNombreContactoAlt"
                  className="mb-3"
                >
                  <Form.Label>Nombres de Contacto Alterno</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="nombresContactoAlterno"
                    value={formState.nombresContactoAlterno.value}
                    onChange={onInputChanged}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.nombresContactoAlterno)}
                    isInvalid={isFormValueInvalid(
                      formState.nombresContactoAlterno
                    )}
                  />
                  <Form.Text className="text-muted">Opcional</Form.Text>
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formCelContactoAlt"
                  className="mb-5"
                >
                  <Form.Label>Celular de Contacto Alterno</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="celularContactoAlterno"
                    value={formState.celularContactoAlterno.value}
                    onChange={onInputChanged}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.celularContactoAlterno)}
                    isInvalid={isFormValueInvalid(
                      formState.celularContactoAlterno
                    )}
                  />
                  <Form.Text muted>Opcional</Form.Text>
                </Form.Group>

                <h5 className="mb-3">Datos de Transporte</h5>
                <Form.Group
                  as={Col}
                  controlId="formDireccionResidencia"
                  className="mb-3"
                >
                  <Form.Label>Dirección de Transporte</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="direccionResidencia"
                    value={formState.direccionResidencia.value}
                    onChange={onInputChanged}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.direccionResidencia)}
                    isInvalid={isFormValueInvalid(
                      formState.direccionResidencia
                    )}
                  />
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formsectorResidencia"
                  className="mb-5"
                >
                  <Form.Label>Sector de Transporte</Form.Label>
                  <Typeahead
                    id="input-sector-residencia"
                    options={sectoresResidencia}
                    labelKey="nombre"
                    onChange={(selected) =>
                      onTypeAheadChanged(
                        selected,
                        "sectorResidencia",
                        sectoresResidencia
                      )
                    }
                    isValid={isFormValueValid(formState.sectorResidencia)}
                    isInvalid={isFormValueInvalid(formState.sectorResidencia)}
                  />
                </Form.Group>

                <h5 className="mb-3">Datos de Mercadeo</h5>
                <Form.Group
                  as={Col}
                  controlId="formCanalMarketing"
                  className="mb-3"
                >
                  <Form.Label>Canal de Marketing</Form.Label>
                  <Form.Check
                    type="radio"
                    id="radio_ig"
                    label="Instagram"
                    name="canalMarketing"
                    value={CanalMarketing.Instagram.toString()}
                    onChange={onInputChanged}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_fb"
                    label="Facebook"
                    name="canalMarketing"
                    value={CanalMarketing.Facebook.toString()}
                    onChange={onInputChanged}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_rec"
                    label="Recomendación"
                    name="canalMarketing"
                    value={CanalMarketing.Recomendacion.toString()}
                    onChange={onInputChanged}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_pi"
                    label="Publicidad Impresa"
                    name="canalMarketing"
                    value={CanalMarketing.PublicidadImpresa.toString()}
                    onChange={onInputChanged}
                  />

                  <Form.Text muted>Opcional</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formEsTurista">
                  <Form.Check
                    id="checkEsTurista"
                    name="esTurista"
                    type="checkbox"
                    label="Es turista"
                    onChange={onCheckChanged}
                    value={formState.esTurista.value.toString()}
                  />
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formPaisProveniencia"
                  className="mb-3"
                >
                  <Form.Label>País de Proveniencia</Form.Label>
                  <Typeahead
                    id="input-pais-proveniencia"
                    options={paises}
                    labelKey="nombre"
                    onChange={(selected) =>
                      onTypeAheadChanged(selected, "paisProveniencia", paises)
                    }
                    disabled={!formState.esTurista.value}
                    isValid={isFormValueValid(formState.paisProveniencia)}
                    isInvalid={isFormValueInvalid(formState.paisProveniencia)}
                  />
                  <Form.Text muted>Opcional y solo para turistas</Form.Text>
                  <Form.Control.Feedback type="invalid">
                    País debe estar en el listado.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  controlId="formCiudadProveniencia"
                  className="mb-5"
                >
                  <Form.Label>Ciudad de Proveniencia</Form.Label>
                  <Typeahead
                    id="input-ciudad-proveniencia"
                    options={ciudadesProveniencia}
                    labelKey="nombre"
                    onChange={(selected) =>
                      onTypeAheadChanged(
                        selected,
                        "ciudadProveniencia",
                        ciudadesProveniencia
                      )
                    }
                    disabled={!formState.esTurista.value}
                    isValid={isFormValueValid(formState.ciudadProveniencia)}
                    isInvalid={isFormValueInvalid(formState.ciudadProveniencia)}
                  />
                  <Form.Text muted>Opcional y solo para turistas</Form.Text>
                  <Form.Control.Feedback type="invalid">
                    Ciudad debe estar en el listado.
                  </Form.Control.Feedback>
                </Form.Group>

                <h5 className="mb-3">Firma</h5>

                <Form.Group
                  as={Col}
                  controlId="formTipoContrato"
                  className="mb-3"
                >
                  <Form.Label>¿Cómo generará el contrato?</Form.Label>
                  <Form.Check
                    type="radio"
                    id="tipo_contrato_firmar"
                    label="Firmando"
                    name="tipo_contrato_firmar"
                    onChange={onToggleFirmar}
                    checked={firmar}
                  />

                  <Form.Check
                    type="radio"
                    id="tipo_contrato_cargar"
                    label="Cargando el archivo firmado"
                    name="tipo_contrato_cargar"
                    onChange={onToggleFirmar}
                    checked={!firmar}
                  />
                </Form.Group>
                {!firmar && (
                  <Form.Group
                    as={Col}
                    controlId="formContrato"
                    xs="auto"
                    className="mb-3"
                  >
                    <Form.Label>Contrato Firmado</Form.Label>
                    <StyledFormControl
                      type="file"
                      name="contrato"
                      onChange={onFileChanged}
                      onBlur={handleBlur}
                      isValid={isFormValueValid(formState.contrato)}
                      isInvalid={isFormValueInvalid(formState.contrato)}
                    />
                    <Form.Text muted>
                      Opcional. Cargar si no se firmará en línea.
                    </Form.Text>
                  </Form.Group>
                )}

                {firmar && (
                  <Row>
                    <ButtonsCol className="d-flex justify-content-start mb-3">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setMostrarModalFirma(true)}
                      >
                        Firmar
                      </Button>
                    </ButtonsCol>
                  </Row>
                )}

                {mostrarModalFirma && (
                  <ModalFirma
                    mostrar={mostrarModalFirma}
                    onCancelar={() => {
                      onSignClean();
                      setMostrarModalFirma(false);
                    }}
                    onLimpiar={onSignClean}
                    onConfirmar={() => {
                      setMostrarModalFirma(false);
                    }}
                    setDataUrl={onSignConfirmed}
                  />
                )}

                <ButtonsCol className="d-flex justify-content-center mt-3">
                  <Button id="buttonFinalizar" type="submit" variant="primary">
                    Finalizar
                  </Button>
                  <SuccessButton
                    id="buttonRegistrarMascota"
                    type="submit"
                    variant="success"
                    onClick={onCrearMascotaButtonClicked}
                  >
                    Registrar Mascota
                  </SuccessButton>
                </ButtonsCol>
              </Form>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </Row>
  );
}

const ButtonsCol = styled(Col)`
  gap: 1%;
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`;

const isFormValueValid = (formValue: FormValue): boolean =>
  formValue.isValid === true && formValue.wasTouched === true;
const isFormValueInvalid = (formValue: FormValue): boolean =>
  formValue.isValid === false && formValue.wasTouched === true;

const buildExitoOptions = (idPropietario: string): ExitoOption[] => {
  return [
    {
      description: "Crear nuevo propietario",
      path: "/propietarios/crear",
      variant: "primary",
    },
    {
      description: "Ver listado",
      path: "/propietarios",
      variant: "secondary",
    },
    {
      description: "Ver propietario",
      path: "/propietarios/" + idPropietario,
      variant: "secondary",
      isLast: true,
    },
  ];
};

export default FormSection;
